/* eslint-disable tailwindcss/no-unnecessary-arbitrary-value */
/* eslint-disable jsx-a11y/no-static-element-interactions */
'use client';

/* eslint-disable tailwindcss/no-arbitrary-value */
import classNames from 'classnames';
import type React from 'react';
import { BaseTranslations } from '../baseTranslations';
import { useCmsClientContext } from '../utils/cmsPageContext';
import { getClientTranslations } from '../utils/translations/getClientTranslations';
import { CmsImage, IMAGE_SIZES } from './CmsImage';
import { Tag } from './Tag';
import Tooltip from './Tooltip';

type RootDivProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'title'>;

interface Props extends RootDivProps {
  title: string;
  subtitle?: string;
  image: string;
  eyebrow?: string;
  tooltip?: string;
  tagText?: string;
  rewardThumbnailSizeClasses?: string;
  urgentTagText?: boolean;
  isFullSize?: boolean;
  blueBadgeText?: string;
}

export const RewardThumbnail = (props: Props) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const {
    className,
    eyebrow,
    tooltip,
    isFullSize,
    urgentTagText,
    tagText,
    image,
    title,
    subtitle,
    rewardThumbnailSizeClasses,
    blueBadgeText,
    ...rootDivProps
  } = props;

  return (
    <Tooltip
      isFullSize={isFullSize}
      tooltipClass="max-w-82 !p-0 shadow-md bg-white md:max-w-111 lg:max-w-[390px]"
      placement="top"
      content={
        tooltip ? (
          <div className=" rounded-md px-spacing/gap/x-sm py-spacing/gap/y-sm bg-white">
            <h3 className="mhg-alpha-typography-badge text-gray-700">
              {eyebrow || staticText.lightdeal}
            </h3>
            <p className="mhg-alpha-typography-bullet-2">{tooltip}</p>
          </div>
        ) : null
      }
    >
      <div
        {...rootDivProps}
        className={classNames(
          'flex cursor-pointer flex-col items-center overflow-hidden rounded-radius/button border border-borders/default bg-white',
          rewardThumbnailSizeClasses,
          className,
          {
            '!w-full': isFullSize,
            'size-35': !rewardThumbnailSizeClasses,
          },
        )}
      >
        {tagText && (
          <div
            className={classNames(
              'flex h-5 w-full shrink-0 items-center justify-start rounded-t-radius/button font-font-weight/overline text-[11px]',
              {
                'bg-highlights/red-light text-highlights/red-dark':
                  !urgentTagText,
                'bg-highlights/red-dark text-highlights/red-light':
                  urgentTagText,
              },
            )}
            title={tagText}
          >
            <div className="w-full truncate px-2 text-center">{tagText}</div>
          </div>
        )}
        <div className="flex min-h-0 w-full flex-1 items-center justify-center p-3">
          <CmsImage
            src={image}
            alt="gift logo"
            width={140}
            height={140}
            sizes={{ _: IMAGE_SIZES.thumbnail }} // RewardThumbnail size is responsive but it always small enough to use thumbnail size
            className="max-h-full max-w-full object-contain"
          />
        </div>
        {blueBadgeText && (
          <div className="pb-3">
            <Tag size="small" color="blue">
              {blueBadgeText}
            </Tag>
          </div>
        )}
        {(title || subtitle) && (
          <div className="flex min-h-[30px] w-full shrink-0 flex-col justify-center rounded-b-radius/button px-spacing/padding/x-xs py-spacing/padding/soft-6px text-center bg-fill/alternative-1">
            {title && (
              <p className="mhg-alpha-typography-badge text-text/secondary truncate">
                {title}
              </p>
            )}
            {subtitle && (
              <p className="truncate text-[9px] md:text-[10px] text-text/secondary-lighter leading-[1.3]">
                {subtitle}
              </p>
            )}
          </div>
        )}
      </div>
    </Tooltip>
  );
};
